import { useMutation } from '@apollo/client';
import {
  CreateRequestMutation,
  CreateRequestMutationVariables,
  CustomerOnboardingState,
  Request,
} from 'lib/generated/graphql';
import { NEW_REQUEST_MUTATION } from 'lib/mutations';
import { FIND_REQUESTS_QUERY } from 'lib/queries';
import { useEffect, useRef, useState } from 'react';
import FormGoals from 'src/screens/company/components/CreateProject/FormGoals';
import ProjectForm from 'src/screens/my-projects/components/CreateLegacyRequest/ProjectForm';
import Stepper from 'src/screens/onboarding/components/Carousel';
import useAuth from 'src/shared-components/Auth';

const steps = [
  {
    position: 0,
    title: 'Tell us what you’re looking for. 🔎',
    subtitle: 'Add the outcomes or things you’re hoping to get help with.',
    action: 'Next - Add some color.',
  },
  {
    position: 1,
    title: 'Add some color. 🎨',
    action: 'Submit',
  },
];

const CreateProject = ({
  companyId,
  onRequestCreated,
  setIsLoading,
}: {
  companyId: number;
  onRequestCreated: (request: Request) => void;
  setIsLoading?: (isLoading: boolean) => void;
}) => {
  const childForms = useRef<any>([]);
  const [formValues, setFormValues] = useState<any>();
  const [currentStep, setCurrentStep] = useState(steps[0]);
  const { user, update } = useAuth();
  const [createRequestMutation, { error }] = useMutation<CreateRequestMutation, CreateRequestMutationVariables>(
    NEW_REQUEST_MUTATION,
    { refetchQueries: [FIND_REQUESTS_QUERY] },
  );

  useEffect(() => {
    if (formValues?.brief) {
      handleCreateProject();
    }
  }, [formValues]);

  const handleButtonClick = () => {
    (childForms.current[currentStep.position] as any).submitForm();
  };

  const handleCreateProject = async () => {
    if (setIsLoading) {
      setIsLoading(true);
    }

    const input = { ...formValues, ...{ company: { connect: { id: companyId } } } };

    try {
      const { data } = await createRequestMutation({
        variables: {
          input,
        },
      });

      if (setIsLoading) {
        setIsLoading(false);
      }
      global?.analytics?.track(`Project Created`, {
        companyId,
      });
      onRequestCreated(data!.createRequest as Request);
    } catch (error) {
      /* istanbul ignore next */
      console.error(error);
      if (setIsLoading) {
        setIsLoading(false);
      }
    }
  };

  const markCustomerOnboardingComplete = async () => {
    await update({
      customerOnboarding: {
        update: {
          state: {
            set: CustomerOnboardingState.Complete,
          },
          completedFirstRequest: {
            set: true,
          },
        },
      },
    });
    global?.analytics.track('Completed Huddle Request');
  };

  if (error) {
    // TODO: show the error
    /* istanbul ignore next */
    console.error(error);
  }

  return (
    <div className="md:w-[720px]">
      <Stepper
        steps={steps}
        currentStep={currentStep.position}
        onButtonClick={handleButtonClick}
        buttonColor="black"
        buttonClass="bg-[#151515]"
        buttonSize="xl"
        titleClassName="text-left mt-5"
        textCenterTitle={false}
        stepTextCenter={false}
        subtitleClass="text-left"
        backButton
        onBackClick={() => setCurrentStep(steps[currentStep.position - 1])}
      >
        {currentStep.position === 0 && (
          <div className="max-h-80 overflow-scroll">
            <FormGoals
              initialValues={formValues?.goals?.connect?.map(goal => goal.id) || []}
              ref={el => (childForms.current[0] = el)}
              onFormSubmitted={values => {
                setFormValues({ ...formValues, ...{ goals: { connect: values } } });
                setCurrentStep(steps[1]);
                global?.analytics.track('Completed Request Goals Step', { ...values });
              }}
            />
          </div>
        )}
        {currentStep.position === 1 && (
          <div className="mt-6">
            <ProjectForm
              initialValues={{
                preferredStartDate: '',
                preferredDuration: '',
                cashBudget: '',
                equityOrCrypto: true,
                brief: '',
              }}
              ref={el => (childForms.current[1] = el)}
              onFormSubmitted={values => {
                setFormValues({ ...formValues, ...values });
                global?.analytics.track('Completed Request Brief Step', { ...values });
                if (!user.isCore) {
                  if (user.customerOnboarding.state !== CustomerOnboardingState.Complete) {
                    markCustomerOnboardingComplete();
                  }
                }
              }}
              update={false}
            />
          </div>
        )}
      </Stepper>
    </div>
  );
};

export default CreateProject;
